import React, { useState, useEffect } from 'react';
import './AddProductForm.css';

const DO_RE = /^[0-9]{6,10}$/;

const AddProductForm = ({ onClose,onProductAdded }) => {
  const [form, setForm] = useState({
    asset1_id: '',               // model id
    asset2_id: '',               // color id
    imei_number: '',
    serial_number: '',
    inventory_type: '',
    delivery_order_number: '',
    location_id: ''
  });
  const token = localStorage.getItem('token');

  const [models, setModels] = useState([]);
  const [colors, setColors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load models & locations once
  useEffect(() => {
    fetch('/api/models')
      .then(r => r.json())
      .then(setModels)
      .catch(e => console.error('Error fetching models:', e));

    fetch('/api/locations')
      .then(r => r.json())
      .then(setLocations)
      .catch(e => console.error('Error fetching locations:', e));
  }, []);

  // Load colors whenever a model is chosen
  useEffect(() => {
    if (!form.asset1_id) { setColors([]); setForm(f => ({ ...f, asset2_id: '' })); return; }
    fetch(`/api/models/${form.asset1_id}/colors`)
      .then(r => r.json())
      .then(rows => {
        setColors(rows);
        // if previous color isn't in new list, clear it
        setForm(f => rows.some(c => String(c.id) === String(f.asset2_id)) ? f : { ...f, asset2_id: '' });
      })
      .catch(e => console.error('Error fetching colors:', e));
  }, [form.asset1_id]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!form.asset1_id) return 'Please select a model.';
    if (!form.asset2_id) return 'Please select a colour.';
    if (!form.serial_number) return 'Serial number is required.';
    if (!form.inventory_type) return 'Type is required.';
    if (!form.location_id) return 'Location is required.';
    if (form.delivery_order_number && !DO_RE.test(String(form.delivery_order_number))) {
      return 'Delivery Order must be 6–10 digits.';
    }
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    const msg = validate();
    if (msg) { setError(msg); return; }

    setSubmitting(true);
    fetch('/api/products', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
       },
      body: JSON.stringify({
        asset1_id: Number(form.asset1_id),
        asset2_id: Number(form.asset2_id),
        imei_number: form.imei_number || null,   // optional in schema
        serial_number: form.serial_number,        // required
        inventory_type: form.inventory_type,
        delivery_order_number: form.delivery_order_number || null,
        location_id: Number(form.location_id)
      }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.message || 'Failed to add product');
        return data;
      })
      .then((data) => {
        console.log('Product added successfully:', data);
        onProductAdded();
      })
      .catch((err) => setError(err.message))
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="modal-overlay-form">
      <div className="modal-content-form">
        {/* Close Button */}
        <button className="close-button" onClick={onClose}>✖</button>

          <h3>Add Product</h3>

          <form onSubmit={handleSubmit}>

            <div className='form-group'>
              <label id="add-user-label">Model:</label>
              <select id="add-user-input"
                name="asset1_id"
                value={form.asset1_id}
                onChange={onChange}
          
                required
              >
                <option value="">Select model</option>
                {models.map(m => (
                  <option key={m.id ?? m.asset1_id} value={m.id ?? m.asset1_id}>
                    {m.name ?? m.asset1_name}
                  </option>
                ))}
              </select>
            </div>

            <div className='form-group'>
              <label className="add-user-label">Colour:</label>
              <select className="add-user-input"
                name="asset2_id"
                value={form.asset2_id}
                onChange={onChange}
                required
                disabled={!form.asset1_id}
              >
                <option value="">{form.asset1_id ? 'Select colour' : 'Select model first'}</option>
                {colors.map(c => (
                  <option key={c.id ?? c.asset2_id} value={c.id ?? c.asset2_id}>
                    {c.name ?? c.asset2_name}
                  </option>
                ))}
              </select>
            </div>

            <div className='form-group'>
              <label id="add-user-label">IMEI Number:</label>
              <input id="add-user-input"
                type="text"
                name="imei_number"
                value={form.imei_number}
                onChange={onChange}
                placeholder="Optional"
              />
            </div>

            <div className='form-group'>
              <label id="add-user-label">Serial Number:</label>
              <input id="add-user-input"
                type="text"
                name="serial_number"
                value={form.serial_number}
                onChange={onChange}
                required
              />
            </div>

            <div className='form-group'>
              <label id="add-user-label">Type:</label>
              <select id="add-user-input"
                name="inventory_type"
                value={form.inventory_type}
                onChange={onChange}
                required
              >
                <option value="">Select Type</option>
                <option value="FOC">FOC</option>
                <option value="PO">PO</option>
              </select>
            </div>

            <div className='form-group'>
              <label id="add-user-label">Delivery Order No.:</label>
              <input id="add-user-input"
                type="text"
                name="delivery_order_number"
                value={form.delivery_order_number}
                onChange={onChange}
                inputMode="numeric"
                pattern="[0-9]{6,10}"
                maxLength={10}
                placeholder="6–10 digits"
                title="6–10 digits"
              />
            </div>

            <div className='form-group'>
              <label id="add-user-label">Location:</label>
              <select id="add-user-input"
                name="location_id"
                value={form.location_id}
                onChange={onChange}
                required
              >
                <option value="">Select a location</option>
                {locations.map((loc) => (
                  <option key={loc.location_id} value={loc.location_id}>
                    {loc.location_name}
                  </option>
                ))}
              </select>
            </div>

            <button type="submit"  className="submit-btn" disabled={submitting}>
              {submitting ? 'Adding…' : 'Add Product'}
            </button>
          </form>
      </div>
    </div>    
  );
};

export default AddProductForm;
